import { validateNumericArray } from './validation.js';

export function mean(arr) {
  validateNumericArray(arr, 'array');
  return arr.reduce((sum, val) => sum + val, 0) / arr.length;
}

export function median(arr) {
  validateNumericArray(arr, 'array');
  const sorted = Array.from(arr).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export function variance(arr, sample = true) {
  validateNumericArray(arr, 'array');
  const m = mean(arr);
  const divisor = sample ? arr.length - 1 : arr.length;
  return arr.reduce((sum, val) => sum + Math.pow(val - m, 2), 0) / divisor;
}

export function standardDeviation(arr, sample = true) {
  return Math.sqrt(variance(arr, sample));
}

export function min(arr) {
  validateNumericArray(arr, 'array');
  return Math.min.apply(null, arr);
}

export function max(arr) {
  validateNumericArray(arr, 'array');
  return Math.max.apply(null, arr);
}

export function sum(arr) {
  validateNumericArray(arr, 'array');
  return arr.reduce((sum, val) => sum + val, 0);
}

export function mode(arr) {
  const frequency = {};
  let maxFreq = 0;
  let modeValue = arr[0];

  for (const val of arr) {
    frequency[val] = (frequency[val] || 0) + 1;
    if (frequency[val] > maxFreq) {
      maxFreq = frequency[val];
      modeValue = val;
    }
  }

  return modeValue;
}

export function quantile(arr, q) {
  validateNumericArray(arr, 'array');
  if (q < 0 || q > 1) {
    throw new Error('Quantile must be between 0 and 1');
  }

  const sorted = Array.from(arr).sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;

  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

export function correlation(x, y) {
  validateNumericArray(x, 'x');
  validateNumericArray(y, 'y');

  if (x.length !== y.length) {
    throw new Error('Arrays must have the same length');
  }

  const n = x.length;
  const meanX = mean(x);
  const meanY = mean(y);

  let numerator = 0;
  let denomX = 0;
  let denomY = 0;

  for (let i = 0; i < n; i++) {
    const diffX = x[i] - meanX;
    const diffY = y[i] - meanY;
    numerator += diffX * diffY;
    denomX += diffX * diffX;
    denomY += diffY * diffY;
  }

  return numerator / Math.sqrt(denomX * denomY);
}

export function zscore(arr) {
  const m = mean(arr);
  const std = standardDeviation(arr);
  return arr.map(val => (val - m) / std);
}
